import React, {
    Fragment,
    useState,
    useContext,
    useEffect,
    useMemo,
} from "react";
import Toolbar, { ToolbarGroup } from "components/Toolbar/Toolbar";
import ToolbarButton from "components/Toolbar/ToolbarButton";
import {
    SaveIcon,
    LockClosedIcon,
    LockOpenIcon,
    EyeIcon,
    BanIcon,
    TrashIcon,
    SupportIcon,
} from "@heroicons/react/outline";
import { ContainerContext } from "context/Container";
import { useAuth } from "hooks/Auth";
import { apiPost, apiDel, apiGet, messageFromException } from "helpers/Request";
import { useFormikContext } from "formik";
import { modal } from "components/HeadlessModal";
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import { removeItem as removeRecentItem } from "redux/actions/recent-items";
import { useContextualHelp } from "hooks/Help";
import { useRegisterActions } from "kbar";
import { toast } from "react-hot-toast";
import DangerButton from "components/Buttons/DangerButton";
import SecondaryButton from "components/Buttons/SecondaryButton";

export default function ContainerToolbar(props) {
    const { state: containerState, setState: setContainerState } =
        useContext(ContainerContext);

    const auth = useAuth();
    const history = useHistory();
    const dispatch = useDispatch();
    const [isLocked, setIsLocked] = useState(false);
    const [isUserEditing, setIsUserEditing] = useState(false);
    const [blockingButton, setBlockingButton] = useState(false);
    const { submitForm } = useFormikContext();
    const contextualHelp = useContextualHelp();

    const allKbarActions = [
        {
            id: "containerCheckout",
            name: "Check Out",
            keywords: "checkout",
            perform: () => {
                checkOut();
            },
        },
        {
            id: "containerCheckin",
            name: "Check In",
            keywords: "checkin",
            perform: () => {
                checkIn();
            },
        },
        {
            id: "containerSaveCheckin",
            name: "Save and Check In",
            keywords: "save checkin",
            perform: () => {
                saveCheckIn();
            },
        },
    ];

    const lockState = useMemo(() => {
        if (!containerState.container || !containerState.container.uuid) {
            return "new";
        }
        if (
            containerState.container.lock &&
            containerState.container.lock.user.uuid === auth.user.uuid
        ) {
            return "locked-self";
        } else if (
            containerState.container.lock &&
            containerState.container.lock.user.uuid !== auth.user.uuid
        ) {
            return "locked-other";
        } else {
            return "available";
        }
    }, [containerState.container, auth.user]);

    const kbarActions = useMemo(() => {
        return allKbarActions.filter((action) => {
            if (
                action.id === "containerCheckout" &&
                lockState === "available"
            ) {
                return true;
            } else if (
                (action.id === "containerCheckin" ||
                    action.id === "containerSaveCheckin") &&
                lockState === "locked-self"
            ) {
                return true;
            }

            return false;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [containerState.container, lockState]);

    useRegisterActions(kbarActions, [kbarActions]);

    const checkOut = () => {
        if (!containerState.container.uuid) {
            return;
        }
        if (containerState.container.lock) {
            return;
        }

        const id = toast.loading("Checking out...");

        setBlockingButton("check-out");
        setContainerState({ preformingStateUpdate: true });
        apiPost("/v4/lock-request", auth.publication, {
            srn: containerState.container.srn,
        })
            .then((resp) => {
                containerState.requestReload();
                setContainerState({ preformingStateUpdate: false });
                setBlockingButton(false);
                toast.success("Checked out", { id });
            })
            .catch((err) => {
                toast.dismiss(id);
                setContainerState({ preformingStateUpdate: false });
                setBlockingButton(false);
                modal.error({
                    title: "Error checking out",
                    message: messageFromException(err),
                });
            });
    };

    const checkIn = () => {
        if (!containerState.container.uuid) {
            return;
        }
        if (
            !containerState.container.lock ||
            containerState.container.lock.user.uuid !== auth.user.uuid
        ) {
            return;
        }

        const id = toast.loading("Checking in...");

        setBlockingButton("check-in");
        setContainerState({ preformingStateUpdate: true });

        apiDel(
            "/v4/lock-request/" + containerState.container.lock.uuid,
            auth.publication
        )
            .then((resp) => {
                containerState.requestReload();
                setContainerState({ preformingStateUpdate: false });
                setBlockingButton(false);
                toast.success("Checked in", { id });
            })
            .catch((err) => {
                setContainerState({ preformingStateUpdate: false });
                setBlockingButton(false);
                toast.dismiss(id);
                modal.error({
                    title: "Error checking in",
                    message: messageFromException(err),
                });
            });
    };

    const saveCheckIn = () => {
        if (!containerState.container.uuid) {
            return;
        }
        if (
            !containerState.container.lock ||
            containerState.container.lock.user.uuid !== auth.user.uuid
        ) {
            return;
        }

        setBlockingButton("save-check-in");
        setContainerState({ preformingStateUpdate: true });
        submitForm().then(() => {
            apiDel(
                "/v4/lock-request/" + containerState.container.lock.uuid,
                auth.publication
            )
                .then((resp) => {
                    containerState.requestReload();
                    setContainerState({ preformingStateUpdate: false });
                    setBlockingButton(false);
                })
                .catch((err) => {
                    toast.error("Unable to release lock");
                });
        });
    };

    const del = () => {
        if (!containerState.container.uuid) {
            return;
        }
        if (
            !containerState.container.lock ||
            containerState.container.lock.user.uuid !== auth.user.uuid
        ) {
            return;
        }

        const uuid = containerState.container.uuid;

        modal.warning({
            title: "Delete Items",
            children: (
                <Fragment>
                    Are you sure you want to delete this and all saved versions?
                    This cannot be undone.
                </Fragment>
            ),
            buttons: () => (
                <Fragment>
                    <DangerButton onClick={() => handleDel(uuid)}>
                        Delete
                    </DangerButton>
                    <SecondaryButton onClick={modal.dismiss}>
                        Cancel
                    </SecondaryButton>
                </Fragment>
            ),
        });
    };

    const handleDel = (uuid) => {
        setBlockingButton("delete");
        setContainerState({ preformingStateUpdate: true });
        modal.update({
            disabled: true,
        });
        const id = toast.loading("Deleting...");

        apiDel(`/v4/container/${uuid}`, auth.publication)
            .then(() => {
                dispatch(removeRecentItem(containerState.container));
                modal.dismiss();
                toast.success("Container deleted", { id });
                history.push("/app/dashboard");
            })
            .catch((err) => {
                setBlockingButton(false);
                setContainerState({ preformingStateUpdate: false });
                toast.dismiss(id);
                modal.error({
                    title: "Unable to delete container",
                    children: messageFromException(err),
                });
            });
    };

    const openPreview = async () => {
        if (!containerState.container.uuid) {
            return;
        }

        const id = toast.loading("Generating preview...");
        const resp = await apiGet(
            `/v4/content/${containerState.container.uuid}/preview/`,
            auth.publication
        );
        if (resp) {
            const url = resp["message"];
            window.open(url, "preview");
            toast.dismiss(id);
        } else {
            toast.dismiss(id);
            modal.error({
                title: "Unable to generate preview",
                children: (
                    <Fragment>
                        Unable to generate preview, this is usually a temporary
                        error. Please try again.
                    </Fragment>
                ),
            });
        }
    };

    useEffect(() => {
        if (!containerState.container || !containerState.container.uuid) {
            setIsLocked(false);
            setIsUserEditing(true);
            return;
        }
        if (
            containerState.container.lock &&
            containerState.container.lock.user.uuid === auth.user.uuid
        ) {
            setIsLocked(true);
            setIsUserEditing(true);
        } else if (
            containerState.container.lock &&
            containerState.container.lock.user.uuid !== auth.user.uuid
        ) {
            setIsLocked(true);
            setIsUserEditing(false);
        } else {
            setIsLocked(false);
            setIsUserEditing(false);
        }
    }, [containerState.container, auth.user]);

    return (
        <Toolbar className="overflow-y-auto">
            <ToolbarGroup>
                <ToolbarButton
                    icon={<SaveIcon />}
                    type="submit"
                    disabled={
                        isUserEditing && !containerState.preformingStateUpdate
                            ? false
                            : true
                    }
                    blocking={containerState.saving && !blockingButton}
                >
                    Save
                </ToolbarButton>
                <ToolbarButton
                    icon={<LockClosedIcon />}
                    blocking={blockingButton === "save-check-in"}
                    onClick={saveCheckIn}
                    disabled={
                        isUserEditing &&
                        containerState.container.uuid &&
                        !containerState.preformingStateUpdate
                            ? false
                            : true
                    }
                    className="hidden xl:flex"
                >
                    Save & Check-in
                </ToolbarButton>
            </ToolbarGroup>
            <ToolbarGroup>
                <ToolbarButton
                    icon={<LockClosedIcon />}
                    disabled={
                        isUserEditing &&
                        containerState.container.uuid &&
                        !containerState.preformingStateUpdate
                            ? false
                            : true
                    }
                    onClick={checkIn}
                    blocking={blockingButton === "check-in"}
                >
                    Check-in
                </ToolbarButton>
                <ToolbarButton
                    icon={<LockOpenIcon />}
                    disabled={
                        !isUserEditing &&
                        !isLocked &&
                        !containerState.preformingStateUpdate
                            ? false
                            : true
                    }
                    onClick={checkOut}
                    blocking={blockingButton === "check-out"}
                >
                    Check-out
                </ToolbarButton>
            </ToolbarGroup>
            <ToolbarGroup>
                <ToolbarButton
                    icon={<EyeIcon />}
                    onClick={openPreview}
                    disabled={!containerState.container.uuid}
                >
                    Preview
                </ToolbarButton>
                <ToolbarButton
                    icon={<BanIcon />}
                    disabled={
                        isLocked &&
                        !containerState.preformingStateUpdate &&
                        auth.user.can("container", "clear-lock")
                            ? false
                            : true
                    }
                    blocking={blockingButton === "unlock"}
                >
                    Unlock
                </ToolbarButton>
                <ToolbarButton
                    icon={<TrashIcon />}
                    onClick={del}
                    disabled={
                        isLocked &&
                        !containerState.preformingStateUpdate &&
                        auth.user.can("container", "delete")
                            ? false
                            : true
                    }
                    blocking={blockingButton === "delete"}
                >
                    Delete
                </ToolbarButton>
            </ToolbarGroup>
            <ToolbarGroup className="ml-auto">
                <ToolbarButton
                    icon={<SupportIcon />}
                    onClick={() => {
                        contextualHelp.showContextualHelp(
                            `/${
                                containerState.container.type === "gallery"
                                    ? "gallery"
                                    : "container"
                            }/index.html`
                        );
                    }}
                    className="text-green-600"
                >
                    Help
                </ToolbarButton>
            </ToolbarGroup>
        </Toolbar>
    );
}
